import { MySection } from '@/components/custom/my-section';
import { Button } from '@/components/ui/button';
import { OrderResponse } from '@/types/order-type';
import { ArrowRight, Calendar, Package } from 'lucide-react';
import { formatCurrency } from '../utils/format-currency';
import { formatDate } from '../utils/format-date';

interface RecentOrderCardProps {
  order?: OrderResponse | null;
  onViewOrder: (orderId: string) => void;
}

export function RecentOrderCard({ order, onViewOrder }: RecentOrderCardProps) {
  if (!order) {
    return (
      <MySection title={'Recent Order'}>
        <div className="text-center py-6 text-gray-500">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm">No orders yet</p>
        </div>
      </MySection>
    );
  }

  // Safely convert order.id to string and get display value
  const orderIdString = order.id != null ? String(order.id) : '';
  const orderDisplay = order.orderNumber || (orderIdString ? orderIdString.substring(0, 8) : 'N/A');

  return (
    <MySection title={'Recent Order'}>
      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Order Number</span>
          <span className="text-sm font-medium text-blue-600">#{orderDisplay}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Date</span>
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(order.createdAt)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Items</span>
          <div className="flex items-center gap-1 text-sm">
            <Package className="h-3 w-3" />
            <span>{order.totalItems || 0} items</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-sm font-semibold text-gray-700">Total Amount</span>
          <span className="text-lg font-bold text-blue-700">
            {formatCurrency(order.totalAmount || '0')}
          </span>
        </div>
      </div>

      <Button variant="outline" className="w-full gap-2" onClick={() => onViewOrder(order.id)}>
        View Full Details
        <ArrowRight className="h-4 w-4" />
      </Button>
    </MySection>
  );
}
